<template>
    <div :class="[{ 'dragging': dragging }]">
        <!-- CALENDAR MODE: block cards -->
        <template v-if="calendarMode">
            <draggable v-model="blocks" handle=".block-drag" :disabled="readonly" ghost-class="ghost">
                <div v-for="(block, i) in blocks" :key="block.uid" class="block-card" :style="blockCardStyle(i)">
                    <div class="block-hdr" :style="blockHdrStyle(i)" @click="toggleBlockOpen(i)">
                        <v-icon v-if="!readonly" class="block-drag" size="18" color="txtMuted" @click.stop
                            @mousedown="dragging = true" @mouseup="dragging = false"
                            @contextmenu.prevent="() => { return false }">mdi-drag-vertical</v-icon>
                        <span class="block-name" :style="{ color: blockColor(i) }">{{ blockName(block, i) }}</span>
                        <span class="block-badge">{{ block.exercises.length }}</span>
                        <button-tooltip v-if="!readonly && !block.isCircuit" :tooltip="$t('tooltips.add_exercise')" top>
                            <template v-slot:activator="{ on, attrs }">
                                <button type="button" class="block-add-ex-btn" v-bind="attrs" v-on="on"
                                    @click.stop="showAddExercisesDialog(block, i)">
                                    <v-icon size="14">mdi-plus</v-icon>
                                </button>
                            </template>
                        </button-tooltip>
                        <v-menu v-if="!readonly" offset-y>
                            <template v-slot:activator="{ on }">
                                <button type="button" class="block-menu-btn" v-on="on" @click.stop>
                                    <v-icon size="16">mdi-dots-vertical</v-icon>
                                </button>
                            </template>
                            <v-list dense>
                                <v-list-item link @click="showChangeBlockName(block, i)">
                                    <v-icon size="18" color="txtSecondary" class="mr-2">mdi-pencil-outline</v-icon>
                                    <v-list-item-title>{{ $t('common.change_name') }}</v-list-item-title>
                                </v-list-item>
                                <v-list-item link @click="duplicateBlock(block, i)">
                                    <v-icon size="18" color="txtSecondary" class="mr-2">mdi-content-copy</v-icon>
                                    <v-list-item-title>{{ $t('common.duplicate') }}</v-list-item-title>
                                </v-list-item>
                                <v-divider />
                                <v-list-item link @click="onDeleteBlock(i)">
                                    <v-icon size="18" color="error" class="mr-2">mdi-trash-can-outline</v-icon>
                                    <v-list-item-title class="error--text">{{ $t('common.delete') }}</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                        <span class="block-chevron" :class="{ open: isBlockOpen(i) }">
                            <v-icon size="16" color="txtMuted">mdi-chevron-down</v-icon>
                        </span>
                    </div>
                    <div v-show="isBlockOpen(i)" class="block-body">
                        <div v-if="block.exercises.length === 0" class="block-empty">
                            {{ $t('exercises.no_exercises_set') }}
                        </div>
                        <draggable v-else v-model="blocks[i].exercises" draggable=".draggable-item"
                            handle=".handle-item" ghost-class="ghost">
                            <div v-for="(exercise, j) in block.exercises" :key="exercise.uid" class="draggable-item">
                                <div class="ex-day-item" :class="{ 'student-edited': !!changesFor(i, exercise) }"
                                    @click="onClickExercise(exercise, i, j)">
                                    <v-icon v-if="!readonly && !block.isCircuit" class="handle-item ex-drag" size="16"
                                        color="txtMuted" @click.stop
                                        @contextmenu.prevent="() => { return false }">mdi-drag-vertical</v-icon>
                                    <div class="ex-thumb" @click.stop="onClickThumb(exercise, i, j)">
                                        <img v-if="exerciseThumb(exercise)" :src="exerciseThumb(exercise)" alt="">
                                        <v-icon v-else size="16" color="txtMuted">mdi-image-outline</v-icon>
                                    </div>
                                    <div class="ex-day-info">
                                        <div class="ex-day-name-row">
                                            <div class="ex-day-name">{{ exercise.name }}</div>
                                            <button-tooltip v-if="changesFor(i, exercise)" variant="rich" tap-to-open
                                                :interactive="hasVideo(changesFor(i, exercise))">
                                                <template v-slot:activator="{ on, attrs }">
                                                    <span class="ex-mod-dot" v-on="on" v-bind="attrs" />
                                                </template>
                                                <template v-slot:tooltip>
                                                    <exercise-change-boxes :changes="changesFor(i, exercise)"
                                                        @click:video="onWatchStudentVideo" />
                                                </template>
                                            </button-tooltip>
                                        </div>
                                        <div class="ex-day-presc">{{ exercisePresc(exercise, block) }}</div>
                                    </div>
                                    <div v-if="!readonly && showSteppers(exercise, block)" class="ex-reorder">
                                        <button-tooltip :tooltip="$t('tooltips.set_add')" left>
                                            <template v-slot:activator="{ on, attrs }">
                                                <button type="button" class="reorder-btn" v-bind="attrs" v-on="on"
                                                    @click.stop="addSeries(i, j)">
                                                    <v-icon size="12">mdi-menu-up</v-icon>
                                                </button>
                                            </template>
                                        </button-tooltip>
                                        <button-tooltip :tooltip="$t('tooltips.set_remove')" left>
                                            <template v-slot:activator="{ on, attrs }">
                                                <button type="button" class="reorder-btn" v-bind="attrs" v-on="on"
                                                    @click.stop="removeSeries(i, j)">
                                                    <v-icon size="12">mdi-menu-down</v-icon>
                                                </button>
                                            </template>
                                        </button-tooltip>
                                    </div>
                                    <div v-if="!readonly && showSteppers(exercise, block)" class="ex-adj-reps">
                                        <button-tooltip :tooltip="$t('tooltips.value_add')" left>
                                            <template v-slot:activator="{ on, attrs }">
                                                <button type="button" class="reorder-btn adj-plus" v-bind="attrs"
                                                    v-on="on" @click.stop="adjustReps(i, j, 1)">
                                                    <v-icon size="12">mdi-plus</v-icon>
                                                </button>
                                            </template>
                                        </button-tooltip>
                                        <button-tooltip :tooltip="$t('tooltips.value_remove')" left>
                                            <template v-slot:activator="{ on, attrs }">
                                                <button type="button" class="reorder-btn adj-minus" v-bind="attrs"
                                                    v-on="on" @click.stop="adjustReps(i, j, -1)">
                                                    <v-icon size="12">mdi-minus</v-icon>
                                                </button>
                                            </template>
                                        </button-tooltip>
                                    </div>
                                    <v-menu v-if="!readonly" offset-y>
                                        <template v-slot:activator="{ on }">
                                            <button type="button" class="ex-day-menu" v-on="on" @click.stop>
                                                <v-icon size="14">mdi-dots-vertical</v-icon>
                                            </button>
                                        </template>
                                        <v-list dense>
                                            <v-list-item link @click="onDuplicateExercise(i, j)">
                                                <v-icon size="18" color="txtSecondary" class="mr-2">mdi-content-copy
                                                </v-icon>
                                                <v-list-item-title>{{ $t('common.duplicate') }}</v-list-item-title>
                                            </v-list-item>
                                            <v-divider />
                                            <v-list-item link @click="onDeleteExercise(i, j)">
                                                <v-icon size="18" color="error" class="mr-2">mdi-trash-can-outline
                                                </v-icon>
                                                <v-list-item-title class="error--text">{{ $t('common.delete')
                                                    }}</v-list-item-title>
                                            </v-list-item>
                                        </v-list>
                                    </v-menu>
                                </div>
                                <rest-card v-if="exercise.hasTime || exercise.time" :rest="exercise"
                                    :readonly="readonly" @click="setRest(block, j)" @click:delete="deleteRest(i, j)" />
                            </div>
                        </draggable>
                    </div>
                </div>
            </draggable>
            <div v-if="!readonly" class="day-left-fab">
                <button-tooltip :tooltip="$t('tooltips.add_block')" top>
                    <template v-slot:activator="{ on, attrs }">
                        <button type="button" class="add-ex-btn" v-bind="attrs" v-on="on" @click="addBlock">
                            <v-icon size="18" color="#0C0E12">mdi-plus</v-icon>
                        </button>
                    </template>
                </button-tooltip>
            </div>
        </template>

        <!-- DEFAULT: block cards -->
        <template v-if="!calendarMode">
            <div v-if="blocks.length === 0" class="blocks-empty">{{ $t('routines.no_blocks_created') }}</div>

            <draggable :disabled="readonly || singleBlock" v-model="blocks" handle=".block-drag" ghost-class="ghost"
                class="blocks-grid" :class="{ 'blocks-grid--cols': !forceCols12 }">
                <div v-for="(block, i) in blocks" :key="block.uid" class="block-card" :style="blockCardStyle(i)">
                    <div class="block-hdr" :style="blockHdrStyle(i)" @click="toggleBlockOpen(i)">
                        <v-icon v-if="!readonly && !singleBlock" class="block-drag" size="18" color="txtMuted" @click.stop
                            @mousedown="dragging = true" @mouseup="dragging = false"
                            @contextmenu.prevent="() => { return false }">mdi-drag-vertical</v-icon>
                        <span class="block-name" :style="{ color: blockColor(i) }">{{ blockName(block, i) }}</span>
                        <span class="block-badge">{{ block.exercises.length }}</span>
                        <button-tooltip v-if="!readonly && !block.isCircuit" :tooltip="$t('tooltips.add_exercise')" top>
                            <template v-slot:activator="{ on, attrs }">
                                <button type="button" class="block-add-ex-btn" v-bind="attrs" v-on="on"
                                    @click.stop="showAddExercisesDialog(block, i)">
                                    <v-icon size="14">mdi-plus</v-icon>
                                </button>
                            </template>
                        </button-tooltip>
                        <v-menu v-if="!readonly && !singleBlock" offset-y>
                            <template v-slot:activator="{ on }">
                                <button type="button" class="block-menu-btn" v-on="on" @click.stop>
                                    <v-icon size="16">mdi-dots-vertical</v-icon>
                                </button>
                            </template>
                            <v-list dense>
                                <v-list-item link @click="showChangeBlockName(block, i)">
                                    <v-icon size="18" color="txtSecondary" class="mr-2">mdi-pencil-outline</v-icon>
                                    <v-list-item-title>{{ $t('common.change_name') }}</v-list-item-title>
                                </v-list-item>
                                <v-list-item link @click="duplicateBlock(block, i)">
                                    <v-icon size="18" color="txtSecondary" class="mr-2">mdi-content-copy</v-icon>
                                    <v-list-item-title>{{ $t('common.duplicate') }}</v-list-item-title>
                                </v-list-item>
                                <v-divider />
                                <v-list-item link @click="onDeleteBlock(i)">
                                    <v-icon size="18" color="error" class="mr-2">mdi-trash-can-outline</v-icon>
                                    <v-list-item-title class="error--text">{{ $t('common.delete') }}</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                        <span class="block-chevron" :class="{ open: isBlockOpen(i) }">
                            <v-icon size="16" color="txtMuted">mdi-chevron-down</v-icon>
                        </span>
                    </div>

                    <div v-show="isBlockOpen(i)" class="block-body">
                        <div v-if="block.exercises.length === 0" class="block-empty">
                            {{ $t('exercises.no_exercises_set') }}
                        </div>
                        <draggable v-else v-model="blocks[i].exercises" draggable=".draggable-item"
                            handle=".handle-item" ghost-class="ghost">
                            <div v-for="(exercise, j) in block.exercises" :key="exercise.uid"
                                class="draggable-item block-exercises">

                                <block-exercise-card :block="blocks[i]" :exercise="exercise" :readonly="readonly"
                                    :is-second-dialog="isSecondDialog"
                                    @click:exercise="onClickExercise($event, i, j)"
                                    @click:duplicate="duplicateExercise($event, j, i)"
                                    @click:change="changeExercise($event, j, i)"
                                    @click:delete="deleteExercise(i, j)" @click:rest="setRest(block, j)"
                                    @update="onExerciseUpdate($event, i, j)"
                                    :calendar-mode="calendarMode" :circuitType="block.type" />

                                <rest-card v-if="exercise.hasTime || exercise.time" :rest="exercise"
                                    :readonly="readonly" @click="setRest(block, j)"
                                    @click:delete="deleteRest(i, j)" />

                            </div>
                        </draggable>
                    </div>
                </div>
            </draggable>

            <div v-if="!readonly && !singleBlock" class="blocks-footer">
                <v-btn depressed height="40" class="normal-btn blocks-add-btn" @click="addBlock">
                    <v-icon size="18" left>mdi-plus</v-icon>
                    {{ $t('tooltips.add_block') }}
                </v-btn>
            </div>
        </template>

        <rest-dialog ref="setRestDialog" @save="onRestSave" />

        <change-name-dialog ref="name" @save="onNameSave" default-names="blockNames" />

        <add-exercises-dialog ref="addExerciseDialog" @close="onAddExercisesClose" :block="activeBlock"
            :title="`${$tc('common.blocks', 1)} ${activeBlockNumber + 1}`" :one-exercise="oneExercise"
            :default-filter="defaultFilter" @change="addExercises" />

        <add-exercise-desktop-dialog v-if="calendarMode" @success="addExercises" :current-selection="currentSelection"
            :current-routines="currentRoutines" :collection="collection" :coach-id="coachId"
            ref="addExerciseCalendarDialog" :routine-number="routineNumber" no-save
            :force-block="activeBlockNumber + 1" />

        <create-circuit-dialog v-if="calendarMode" ref="editCircuitDialog" calendar-mode no-save
            :collection="collection" :coach-id="coachId" @success="onEditCircuitLocal" />

        <kimsa-confirm ref="deleteConfirm" :title="deleteConfirmTitle" :content="deleteConfirmContent"
            @confirm="onConfirmDelete" @cancel="pendingDelete = null" :alert-mode="true" show-cancel
            :confirm-label="$t('common.delete')" />

        <watch-video-dialog :video="videoData.video" :thumb="videoData.thumb" ref="watchVideo" />

    </div>
</template>

<script>

import AddExercisesDialog from '@/components/coach/routines/AddExercisesDialog'
import draggable from 'vuedraggable'
import { getMergedBlocks, exerciseSetsLabel } from "@/utils/Exercises";
import { getBlockColor } from "@/utils/BlockColors";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import ButtonTooltip from "@/components/common/ButtonTooltip";
import WatchVideoDialog from "@/components/common/routines/WatchVideoDialog";
import RestDialog from "@/components/coach/routines/RestDialog";
import RestCard from "@/components/coach/routines/RestCard";
import BlockExerciseCard from "@/components/coach/routines/BlockExerciseCard";
import { randomString } from "@/utils/Common";
import ChangeNameDialog from './ChangeNameDialog.vue';
import AddExerciseDesktopDialog from './AddExerciseDesktopDialog.vue';
import CreateCircuitDialog from './CreateCircuitDialog.vue';
import ExerciseChangeBoxes from '@/components/common/ExerciseChangeBoxes';
import { studentChangeKey } from '@/utils/Routine';

export default {
    props: {
        defaultBlocks: {
            type: Array,
        },
        studentChanges: {
            type: Map,
            default: null,
        },
        readonly: {
            type: Boolean,
            default: false
        },
        singleBlock: {
            type: Boolean,
            default: false
        },
        forceCols12: {
            type: Boolean,
            default: false
        },
        isSecondDialog: {
            type: Boolean,
            default: false
        },
        calendarMode: {
            type: Boolean,
            default: false
        },
        currentSelection: {
            type: Object,
            default: null,
        },
        currentRoutines: {
            type: Array,
            default: null,
        },
        collection: {
            type: String,
            default: null,
        },
        coachId: {
            type: String,
            default: null,
        },
        routineNumber: {
            type: Number,
            default: 1,
        },
        date: {
            type: String,
            default: null,
        },
        assignedRoutineId: {
            type: String,
            default: null,
        },
    },
    components: {
        ExerciseChangeBoxes,
        BlockExerciseCard,
        RestCard,
        RestDialog,
        AddExercisesDialog,
        draggable,
        ChangeNameDialog,
        AddExerciseDesktopDialog,
        CreateCircuitDialog,
        KimsaConfirm,
        ButtonTooltip,
        WatchVideoDialog,
    },
    data() {
        return {
            dragging: false,
            showAddBtn: false,
            blocks: [{ exercises: [] }],
            activeBlock: null,
            activeExerciseIndex: null,
            activeBlockNumber: 0,
            oneExercise: false,
            defaultFilter: null,
            exerciseIndex: null,
            openFirst: [0],
            pendingDelete: null,
            videoData: { video: null, isYoutube: false, thumb: null },
        }
    },
    mounted() {
        const vm = this
        vm.setDefaults()
        setTimeout(() => {
            this.openFirst = [0]
        }, 100)
    },
    computed: {
        deleteConfirmTitle(vm = this) {
            return vm.pendingDelete?.type === 'block' ? vm.$t('common.delete_block') : vm.$t('exercises.delete_exercise')
        },
        deleteConfirmContent(vm = this) {
            if (vm.pendingDelete?.type === 'block') return vm.$t('common.confirm_deleted_block')
            const name = vm.blocks?.[vm.pendingDelete?.i]?.exercises?.[vm.pendingDelete?.j]?.name || ''
            return vm.$t('exercises.confirm_delete_exercise', { exercise: name })
        },
    },
    methods: {
        addSeries(i, j) {
            const sets = this.blocks[i].exercises[j].config?.sets
            if (!sets?.length) return
            sets.push(JSON.parse(JSON.stringify(sets[sets.length - 1])))
        },
        removeSeries(i, j) {
            const sets = this.blocks[i].exercises[j].config?.sets
            if (!sets?.length) return
            if (sets.length <= 1) return this.toast(this.$t('common.cannot_delete_last_set'), 'error')
            sets.splice(sets.length - 1, 1)
        },
        adjustReps(i, j, delta) {
            const sets = this.blocks[i].exercises[j].config?.sets
            if (!sets?.length) return
            sets.forEach(set => {
                if (set.data?.[0]) set.data[0].value = Math.max(1, (Number(set.data[0].value) || 0) + delta)
            })
        },
        exercisePresc(exercise, block) {
            let label = exerciseSetsLabel(exercise, block)
            if (exercise.eachSide) label += ` · ${this.$t('exercises.each_side')}`
            return label
        },
        exerciseThumb(exercise) {
            return exercise.img || exercise?.video?.thumb || null
        },
        showSteppers(exercise, block) {
            return !block?.isCircuit && (exercise?.config?.sets?.length > 0)
        },
        onDuplicateExercise(i, j) {
            const template = JSON.parse(JSON.stringify(this.blocks[i].exercises[j]))
            template.uid = randomString()
            this.blocks[i].exercises.splice(j, 0, template)
        },
        async onEditCircuitLocal({ block, blockIndex }) {
            const vm = this
            const uid = vm.blocks[blockIndex]?.uid
            const [merged] = await getMergedBlocks([block])
            vm.blocks.splice(blockIndex, 1, { ...merged, uid: uid || merged.uid })
        },
        hasVideo(changes) {
            return (changes || []).some(change => change.type === 'video' && !change.expired)
        },
        changesFor(blockIndex, exercise) {
            if (!this.studentChanges) return null
            const byId = studentChangeKey(blockIndex + 1, exercise)
            return (byId && this.studentChanges.get(byId)) || this.studentChanges.get(`${blockIndex + 1}|${exercise?.name}`) || null
        },
        onWatchStudentVideo(change) {
            const vm = this
            if (!change?.url) return
            vm.videoData = { video: change.url, isYoutube: false, thumb: null }
            vm.$nextTick(() => vm.$refs.watchVideo.show(false))
        },
        onClickThumb(exercise, i, j) {
            const vm = this
            const isYoutube = !!exercise.youtubeUrl
            const video = isYoutube ? exercise.youtubeUrl : (exercise.video?.url || null)
            if (!video) return vm.onClickExercise(exercise, i, j)
            vm.videoData = { video, isYoutube, thumb: vm.exerciseThumb(exercise) }
            vm.$nextTick(() => vm.$refs.watchVideo.show(isYoutube))
        },
        onDeleteExercise(i, j) {
            this.pendingDelete = { type: 'exercise', i, j }
            this.$refs.deleteConfirm.show()
        },
        onDeleteBlock(i) {
            if (this.blocks[i]?.exercises?.length > 0) {
                this.pendingDelete = { type: 'block', i }
                this.$refs.deleteConfirm.show()
            } else {
                this.deleteBlock(i)
            }
        },
        onConfirmDelete() {
            const p = this.pendingDelete
            if (!p) return
            if (p.type === 'exercise') this.deleteExercise(p.i, p.j)
            else this.deleteBlock(p.i)
            this.pendingDelete = null
        },
        onAddExercisesClose() {
            const vm = this
            vm.$emit('close:exercises-dialog')
        },
        async setDefaults(keepOpen = false) {
            const vm = this
            if ((!vm.defaultBlocks || vm.defaultBlocks.length === 0) && !vm.calendarMode) return
            vm.blocks = await getMergedBlocks(vm.defaultBlocks)
            if (keepOpen) return
            setTimeout(() => {
                this.openFirst = [0]
            }, 100)
        },
        onExerciseUpdate(patch, blockIndex, exerciseIndex) {
            const vm = this
            let template = { ...vm.blocks[blockIndex].exercises[exerciseIndex], ...patch }
            vm.blocks[blockIndex].exercises.splice(exerciseIndex, 1, template)
        },
        deleteExercise(blockIndex, exerciseIndex) {
            const vm = this
            vm.blocks[blockIndex].exercises.splice(exerciseIndex, 1)
        },
        deleteRest(blockIndex, exerciseIndex) {
            const vm = this
            vm.blocks[blockIndex].exercises[exerciseIndex].hasTime = false
            vm.blocks[blockIndex].exercises[exerciseIndex].time = ''
        },
        duplicateExercise(exercise, exerciseIndex, blockIndex) {
            const vm = this
            vm.blocks[blockIndex].exercises.splice(exerciseIndex, 0, exercise)
        },
        changeExercise(exercise, exerciseIndex, blockIndex) {
            const vm = this
            vm.activeBlock = null
            vm.activeBlockNumber = blockIndex
            vm.exerciseIndex = exerciseIndex
            vm.oneExercise = true
            vm.defaultFilter = { categories: exercise.categories?.[0], types: exercise.types }
            vm.$refs.addExerciseDialog.show()
        },
        onRestSave(time) {
            const vm = this
            vm.activeBlock.exercises[vm.activeExerciseIndex].hasTime = true
            vm.activeBlock.exercises[vm.activeExerciseIndex].time = time
        },
        setRest(block, exerciseIndex = null) {
            const vm = this
            vm.activeBlock = block

            if (exerciseIndex >= 0) {
                vm.activeExerciseIndex = exerciseIndex
                let time = block.exercises[exerciseIndex]?.time || '00:00:00'
                vm.$refs.setRestDialog.showEdit(time)
            } else {
                vm.$refs.setRestDialog.show()
            }
        },
        addExercises(exercises) {
            const vm = this

            if (vm.oneExercise) {
                let oldExercise = vm.blocks[vm.activeBlockNumber].exercises[vm.exerciseIndex]
                let currentexercise = exercises[0]
                if (oldExercise?.categories?.[0] === currentexercise?.categories?.[0] && !vm.calendarMode) {
                    currentexercise = {...exercises[0], config: oldExercise.config}
                }
                return vm.blocks[vm.activeBlockNumber].exercises.splice(vm.exerciseIndex, 1, { ...currentexercise, type: 'exercise', uid: randomString() })
            }

            let newExercises = exercises.map(ex => ({ ...ex, type: 'exercise', uid: randomString() }))
            vm.activeBlock.exercises = [...vm.activeBlock.exercises, ...newExercises]
        },
        addBlock() {
            const vm = this
            vm.blocks.push({ uid: randomString(), exercises: [] })
            vm.openFirst.push(vm.blocks.length - 1)
            vm.scrollToEnd()
        },
        deleteBlock(index) {
            const vm = this
            vm.blocks.splice(index, 1)
        },
        duplicateBlock(block, index) {
            const vm = this
            let template = JSON.parse(JSON.stringify(block))
            template.uid = randomString()
            vm.blocks.splice(index, 0, template)
            vm.scrollToEnd()
        },
        showAddExercisesDialog(block, number) {
            const vm = this
            vm.activeBlock = block
            vm.activeBlockNumber = number
            vm.oneExercise = false
            vm.exerciseIndex = null
            if (vm.calendarMode) {
                return vm.$refs.addExerciseCalendarDialog.show(vm.date, { assignedRoutineId: vm.assignedRoutineId, block: vm.blocks.length })
            }
            vm.$refs.addExerciseDialog.show()
        },
        onClickExercise(exercise, blockIndex, exerciseIndex) {
            const vm = this
            if (vm.calendarMode) {
                vm.activeBlock = vm.blocks[blockIndex]
                vm.activeBlockNumber = blockIndex
                vm.exerciseIndex = exerciseIndex
                vm.oneExercise = true
                exercise = {
                    ...exercise,
                    exerciseIndex: exerciseIndex,
                    block: blockIndex + 1,
                    oldBlock: blockIndex + 1,
                    lastBlock: vm.blocks.length,
                    date: vm.date,
                }

                if (vm.activeBlock?.isCircuit) {
                    const routineLike = { blocks: vm.blocks, id: vm.assignedRoutineId, routineId: null, rawDate: { full: vm.date } }
                    return vm.$refs.editCircuitDialog.setEditingModeFromCalendar(exercise, routineLike)
                }
                return vm.$refs.addExerciseCalendarDialog.setEditingMode(exercise)
            }
            return vm.$emit('click:exercise', exercise)
        },
        scrollToEnd() {
            const vm = this
            vm.$vuetify.goTo(document.body.scrollHeight)
        },
        showChangeBlockName(block, index) {
            const vm = this
            vm.$refs.name.show(block, index)
        },
        onNameSave(payload) {
            const vm = this
            vm.blocks[payload.index].name = payload.name
        },
        blockName(block, i) {
            return this.blockNameLabel(block.name) || `${this.$tc('common.blocks', 1)} ${i + 1}`
        },
        isBlockOpen(i) {
            return this.openFirst.includes(i)
        },
        toggleBlockOpen(i) {
            const idx = this.openFirst.indexOf(i)
            if (idx === -1) this.openFirst.push(i)
            else this.openFirst.splice(idx, 1)
        },
        blockColor(i) {
            return getBlockColor(i)
        },
        hexToRgba(hex, alpha) {
            const n = parseInt(hex.slice(1), 16)
            return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
        },
        blockCardStyle(i) {
            return { borderColor: this.hexToRgba(this.blockColor(i), 0.3) }
        },
        blockHdrStyle(i) {
            return { background: this.hexToRgba(this.blockColor(i), 0.07) }
        },
    },
    watch: {
        blocks: {
            deep: true,
            handler() {
                this.$emit('change', this.blocks)
            },
        },
    },
}
</script>

<style scoped>
.blocks-empty {
    padding: 24px 0;
    text-align: center;
    font-size: var(--fs-body);
    color: var(--v-txtMuted-base);
}

.blocks-grid {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

@media (min-width: 1264px) {
    .blocks-grid--cols {
        display: grid;
        grid-template-columns: 1fr 1fr;
        align-items: start;
    }
}

.blocks-footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 12px;
}

.blocks-add-btn {
    border-radius: 10px !important;
    background: rgba(51, 153, 153, 0.08) !important;
    border: 1px solid rgba(51, 153, 153, 0.3);
    color: var(--v-accent-base) !important;
}

.blocks-add-btn >>> .v-icon {
    color: var(--v-accent-base) !important;
}

.ghost {
    width: 100%;
    overflow: hidden;
    border-radius: 8px;
    background: var(--v-surface2-base);
    border: 1px dashed var(--v-accent-base);
    opacity: 0.6;
}

.block-exercises {
    border-radius: 8px;
}

.block-exercises:not(:last-child) {
    margin-bottom: 8px;
}

/* CALENDAR MODE — block cards */
.block-card {
    margin-bottom: 8px;
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    background: var(--v-bg-base);
}

.block-hdr {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 10px 10px 6px;
    border-radius: 10px 10px 0 0;
    cursor: pointer;
    user-select: none;
}

.block-drag {
    flex-shrink: 0;
    cursor: grab;
}

.block-name {
    flex: 1;
    min-width: 0;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: -0.01em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.block-badge {
    flex-shrink: 0;
    padding: 1px 7px;
    border-radius: 5px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    color: var(--v-txtMuted-base);
    font-size: 11px;
    font-weight: 600;
}

.block-add-ex-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 5px;
    background: transparent;
    border: 1.5px solid var(--v-outline2-base);
    color: var(--v-txtMuted-base);
    cursor: pointer;
    flex-shrink: 0;
    transition: border-color .15s, color .15s, background .15s;
}

.block-add-ex-btn:hover {
    border-color: var(--v-accent-base);
    color: var(--v-accent-base);
}

.block-menu-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 5px;
    background: transparent;
    border: none;
    color: var(--v-txtMuted-base);
    cursor: pointer;
    flex-shrink: 0;
}

.block-menu-btn:hover {
    background: var(--v-surface2-base);
    color: var(--v-txtSecondary-base);
}

.block-chevron {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: transform .2s;
}

.block-chevron.open {
    transform: rotate(180deg);
}

.block-body {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px;
}

.block-empty {
    padding: 8px;
    text-align: center;
    font-size: 12px;
    color: var(--v-txtMuted-base);
}

.day-left-fab {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 6px 0 2px;
}

.add-ex-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--v-primary-base), var(--v-accent-base));
    border: none;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    transition: transform .15s;
}

.add-ex-btn:hover {
    transform: scale(1.08);
}

/* CALENDAR MODE — exercise items */
.ex-day-item {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 8px;
    border-radius: 8px;
    border: 1px solid transparent;
    cursor: pointer;
    transition: border-color .15s, background .15s;
}

.ex-day-item:hover {
    border-color: var(--v-outline2-base);
    background: var(--v-surface-base);
}

.ex-day-item.student-edited {
    border-color: rgba(232, 92, 92, .35);
    background: rgba(232, 92, 92, .06);
}

.ex-day-item.student-edited:hover {
    border-color: rgba(232, 92, 92, .5);
    background: rgba(232, 92, 92, .1);
}

.ex-drag {
    flex-shrink: 0;
    cursor: grab;
}

.ex-thumb {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 7px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    overflow: hidden;
    flex-shrink: 0;
}

.ex-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.ex-day-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
}

.ex-day-name-row {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
}

.ex-day-name {
    font-size: 12px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.ex-mod-dot {
    position: relative;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--v-error-base);
    box-shadow: 0 0 0 3px rgba(232, 92, 92, .18);
    flex-shrink: 0;
    cursor: pointer;
    animation: exModPulse 2s ease-in-out infinite;
}

.ex-mod-dot::after {
    content: '';
    position: absolute;
    inset: -12px;
}

@keyframes exModPulse {

    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: .45;
    }
}

.ex-day-presc {
    font-size: 10px;
    color: var(--v-txtMuted-base);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.ex-reorder,
.ex-adj-reps {
    display: none;
    flex-direction: column;
    gap: 2px;
    flex-shrink: 0;
}

.ex-day-menu {
    display: none;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 4px;
    background: transparent;
    border: none;
    color: var(--v-txtMuted-base);
    cursor: pointer;
    flex-shrink: 0;
}

.ex-day-item:hover .ex-reorder,
.ex-day-item:hover .ex-adj-reps,
.ex-day-item:hover .ex-day-menu {
    display: flex;
}

.reorder-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 4px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface2-base);
    color: var(--v-txtSecondary-base);
    cursor: pointer;
    transition: border-color .15s, color .15s, background .15s;
}

.reorder-btn:hover {
    border-color: var(--v-outline2-base);
    color: var(--v-txtPrimary-base);
}

.reorder-btn.adj-plus:hover {
    border-color: var(--v-accent-base);
    color: var(--v-accent-base);
}

.reorder-btn.adj-minus:hover {
    border-color: var(--v-error-base);
    color: var(--v-error-base);
}
</style>